// Design tokens — Phase 3 Plan 03-07 Task 07.2 (UI-SPEC §Spacing Scale).
//
// Centralized spacing + typography + touch-target constants для consistency
// across RecipientsTab, CMRReview, BboxCanvas, RecipientsFieldset. UI-SPEC
// §Touch Targets references TOUCH_TARGET_MIN = 44px (Apple HIG minimum для
// reliable touch input on small devices).

export const SPACING = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
} as const;

export const TYPOGRAPHY = {
  body: { size: 16, weight: 400, lineHeight: 1.5 },
  label: { size: 14, weight: 600, lineHeight: 1.4 },
  heading: { size: 20, weight: 600, lineHeight: 1.3 },
  display: { size: 28, weight: 600, lineHeight: 1.2 },
} as const;

/**
 * Minimum touch-target dimension в px (Apple HIG / Material design baseline).
 * BboxCanvas affordances (Clear/Publish/Skip buttons), RecipientsTab Revoke/
 * Reissue buttons MUST не быть меньше этого размера.
 */
export const TOUCH_TARGET_MIN = 44;

export type SpacingToken = keyof typeof SPACING;
export type TypographyToken = keyof typeof TYPOGRAPHY;

// ============================================================================
// Phase 4 Token Extensions (UI-SPEC §Phase 4 Token Extensions, verbatim).
//
// Net-new tokens — SPACING/TYPOGRAPHY/TOUCH_TARGET_MIN above stay Phase 3 verbatim.
// ============================================================================

/**
 * DIS-15 + D-74 color FSM for trip markers on Dashboard map.
 * Same age thresholds as D-74 dispatcher alerts so map color and alert toast agree.
 */
export const TRIP_STATUS_COLORS = {
  green: { fill: "#059669", fillDark: "#34D399" }, // healthy, gps_age < 5min
  yellow: { fill: "#D97706", fillDark: "#FBBF24" }, // 5min <= gps_age < 60min
  red: { fill: "#DC2626", fillDark: "#F87171" }, // problem OR gps_age >= 60min
  gray: { fill: "#94A3B8", fillDark: "#64748B" }, // completed (completed-tab only)
} as const;

export const TRIP_GPS_AGE_THRESHOLDS = {
  greenMaxSeconds: 5 * 60, // <5min = green
  yellowMaxSeconds: 60 * 60, // <60min = yellow; >=60min = red
} as const;

/**
 * D-74 alert toast severity palette (mirrors Phase 3 GPS-lost banner palette).
 */
export const ALERT_SEVERITY_COLORS = {
  warning: {
    bg: "#FEF3C7",
    bgDark: "#78350F",
    border: "#D97706",
    text: "#92400E",
    textDark: "#FCD34D",
  },
  destructive: {
    bg: "#FEE2E2",
    bgDark: "#7F1D1D",
    border: "#DC2626",
    text: "#991B1B",
    textDark: "#FCA5A5",
  },
} as const;

/**
 * Driver-status pill colors (DIS-05 Discretion).
 */
export const DRIVER_STATUS_COLORS = {
  online: { bg: "#D1FAE5", bgDark: "#064E3B", text: "#065F46", textDark: "#34D399" },
  offline: { bg: "#F1F5F9", bgDark: "#1E293B", text: "#64748B", textDark: "#94A3B8" },
  activeTrip: { bg: "#DBEAFE", bgDark: "#1E3A8A", text: "#1E40AF", textDark: "#93C5FD" },
} as const;

/**
 * Excel import wizard step indicator.
 */
export const WIZARD_STEP_COLORS = {
  done: { bg: "#F1F5F9", bgDark: "#1E293B", text: "#475569", textDark: "#94A3B8" },
  active: { bg: "#1E63CE", text: "#FFFFFF" },
  pending: {
    bg: "transparent",
    text: "#94A3B8",
    textDark: "#64748B",
    border: "#E2E8F0",
    borderDark: "#334155",
  },
} as const;

/**
 * R1 delay overlay (UI-SPEC §5, Onfleet "gold delay dot").
 *
 * A delay is a SEPARATE gold layer rendered ON TOP of the base trip-status color
 * (green/yellow/red/gray) — it NEVER replaces the base status. So a delayed but
 * GPS-healthy trip stays green AND gains the gold overlay; a delayed problem trip
 * stays red AND gains the gold overlay. This mirrors Onfleet where the status pin
 * color and the "delayed" gold corner-dot are independent signals.
 *
 * `fill` (light) / `fillDark` (dark) are the gold marker/dot fills; `outline` is the
 * white ring drawn around the dot so it stays visible on any base color underneath.
 */
export const DELAY_OVERLAY = {
  fill: "#D4A017", // gold (light appearance)
  fillDark: "#F5C542", // brighter gold (dark appearance)
  outline: "#FFFFFF", // ring so the gold dot reads on any base color
} as const;

export type TripStatusColor = keyof typeof TRIP_STATUS_COLORS;
export type AlertSeverity = keyof typeof ALERT_SEVERITY_COLORS;
export type DriverStatus = keyof typeof DRIVER_STATUS_COLORS;
